import { IPaginatedResponse } from '../../types/paginated-response.type'
import { IError } from '../base/error'
import ServiceBase from '../base/service.base'
import { IItemTraceability } from '../traceability/traceability.types'

import {
  CreateSingleItemDTO,
  ItemResponseDTO,
  ItemTraceabilityCreateDTO,
  ItemTraceabilityCreateRequestDTO,
} from './items.types'

export class Items extends ServiceBase {
  public async getItem(id: string): Promise<ItemResponseDTO> {
    const url = `/item/${id}`
    const response = await this.request.get<ItemResponseDTO, IError>(url)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async getPendingItems(
    page = 1,
    search?: string,
  ): Promise<IPaginatedResponse<ItemResponseDTO[]>> {
    const url = `/item/pending`
    const response = await this.request.get<
      IPaginatedResponse<ItemResponseDTO[]>,
      IError
    >(url, {
      params: {
        search: search || undefined,
        page,
        limit: 12,
      },
    })

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async getCompleteItems(
    page = 1,
    limit = 10,
    search?: string,
  ): Promise<IPaginatedResponse<ItemResponseDTO[]>> {
    const url = `/item/complete`
    const response = await this.request.get<
      IPaginatedResponse<ItemResponseDTO[]>,
      IError
    >(url, {
      params: {
        search: search || undefined,
        page,
        limit,
      },
    })

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async createItemTraceability(
    itemId: string,
    tree: ItemTraceabilityCreateDTO[],
  ): Promise<void> {
    const url = `/item/origin`

    const response = await this.request.post<void, IError>(url, {
      itemId,
      originItems: tree,
    } as ItemTraceabilityCreateRequestDTO)

    if (response?.status && response?.status === 201) {
      return
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async uploadSheet(itemId: string, file: File) {
    const url = `/item/origin/file/${itemId}`

    const formData = new FormData()
    formData.append('file', file)

    const response = await this.request.post<void, IError>(url, formData)

    if (response?.status && response?.status === 201) {
      return
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async getItemToAudit(id: string): Promise<IItemTraceability[]> {
    const url = `/item/audit/${id}`
    const response = await this.request.get<IItemTraceability[], IError>(url)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async createItem(newItemData: CreateSingleItemDTO): Promise<void> {
    const url = `/item`

    const response = await this.request.post<void, IError>(url, newItemData)

    if (response?.status && response?.status === 201) {
      return
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async uploadItemEvidence(file: File): Promise<string> {
    const url = `/item/evidence/file`

    const formData = new FormData()
    formData.append('file', file)

    const response = await this.request.post<string, IError>(url, formData)

    if (!response.result) {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }

    return response.result
  }
}
